import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

import { EMPTY, forkJoin, switchMap, catchError, finalize, map } from 'rxjs';

import { Benutzer } from '../../models/benutzer.model';
import { Rolle } from '../../models/rolle.model';
import { Filiale } from '../../models/filiale.model';

import {
  BenutzerFormDialogComponent,
  BenutzerFormDialogResult,
} from '../../components/benutzer-form-dialog/benutzer-form-dialog.component';

import {
  RollenAendernDialogComponent,
  RollenAendernDialogResult,
} from '../../components/rollen-aendern-dialog/rollen-aendern-dialog.component';

import { RollenTabComponent } from '../rollen-tab/rollen-tab.component';

import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';

import {
  BenutzerverwaltungService,
  BulkAssignRolesResponse,
} from '../../services/benutzerverwaltung.service';

import { AuthService } from '../../../../core/services/auth.service';
import { BestellungenService } from '../../../bestellungen/services/bestellungen.service';

import {
  ErrorDialogComponent,
  ErrorDialogData,
} from '../../../../shared/components/error-dialog/error-dialog.component';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';

type BenutzerverwaltungTab = 'benutzer' | 'rollen';

@Component({
  selector: 'app-benutzerverwaltung',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSnackBarModule,
    RollenTabComponent,
    MatSlideToggleModule,
  ],
  templateUrl: './benutzerverwaltung.component.html',
  styleUrl: './benutzerverwaltung.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenutzerverwaltungComponent {
  private readonly benutzerverwaltungService = inject(BenutzerverwaltungService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly authService = inject(AuthService);
  private readonly bestellungenService = inject(BestellungenService);

  protected readonly activeTab = signal<BenutzerverwaltungTab>('benutzer');
  protected readonly benutzer = signal<Benutzer[]>([]);
  protected readonly rollen = signal<Rolle[]>([]);
  protected readonly filialen = signal<Filiale[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly selectedIds = signal<string[]>([]);
  protected readonly skeletonRows = Array.from({ length: 6 });
  protected readonly searchTerm = signal('');
  protected readonly hasSystemRoles = signal(true);
  protected readonly isBestellungSettingsLoading = signal(false);
  protected readonly isBestellungSettingsSaving = signal(false);
  protected readonly orderCanBeChangedContinuously = signal(false);

  private hasCheckedSystemRoles = false;

  protected readonly filteredBenutzer = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();

    if (!term) {
      return this.benutzer();
    }

    return this.benutzer().filter((user) => {
      const benutzername = user.benutzername.toLowerCase();
      const name = user.name.toLowerCase();
      const email = user.email.toLowerCase();

      return (
        benutzername.includes(term) ||
        name.includes(term) ||
        email.includes(term)
      );
    });
  });

  constructor() {
    this.loadInitialData();

    if (this.canManageBestellungSettings()) {
      this.loadBestellungSettings();
    }

    if (!this.authService.isCopagoAdmin() && this.authService.isSuperAdmin()) {
      this.checkSystemRolesOnce();
    }
  }

  protected updateSearchTerm(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  protected setActiveTab(tab: BenutzerverwaltungTab): void {
    if (tab === 'benutzer' && this.hasNoRoles()) {
      return;
    }

    this.activeTab.set(tab);

    if (tab === 'benutzer') {
      this.loadInitialData();
    }
  }

  protected createBenutzer(): void {
    if (this.isCopagoAdminMode()) {
      let submittedEmail = '';

      const dialogRef = this.dialog.open(BenutzerFormDialogComponent, {
        width: '720px',
        maxWidth: 'calc(100vw - 32px)',
        panelClass: 'user-dialog-panel',
        autoFocus: false,
        restoreFocus: false,
        disableClose: true,
        data: {
          mode: 'create',
          rollen: [],
          filialen: [],
          isCopagoAdminMode: true,
          submitRequest: (result: BenutzerFormDialogResult) => {
            submittedEmail = result.email;

            return this.benutzerverwaltungService.createSuperAdmin({
              tenantId: result.tenantId ?? '',
              email: result.email,
              password: result.password,
              vorname: result.vorname,
              name: result.name,
              benutzername: result.benutzername,
            });
          },
        },
      });

      dialogRef.afterClosed().subscribe((saved) => {
        if (!saved) {
          return;
        }

        this.loadInitialData();

        this.snackBar.open(`SuperAdmin "${submittedEmail}" wurde erstellt.`, '×', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar'],
        });
      });

      return;
    }

    forkJoin({
      rollen: this.benutzerverwaltungService.getRollen(true),
      filialen: this.benutzerverwaltungService.getFilialen(),
    }).subscribe(({ rollen, filialen }) => {
      this.rollen.set(rollen);
      this.filialen.set(filialen);

      let submittedEmail = '';

      const dialogRef = this.dialog.open(BenutzerFormDialogComponent, {
        width: '720px',
        maxWidth: 'calc(100vw - 32px)',
        panelClass: 'user-dialog-panel',
        autoFocus: false,
        restoreFocus: false,
        disableClose: true,
        data: {
          mode: 'create',
          rollen,
          filialen,
          submitRequest: (result: BenutzerFormDialogResult) => {
            submittedEmail = result.email;

            if (!result.filialen.length) {
              const confirmRef = this.dialog.open<
                ConfirmDialogComponent,
                ConfirmDialogData,
                boolean
              >(ConfirmDialogComponent, {
                width: '460px',
                maxWidth: 'calc(100vw - 32px)',
                panelClass: 'role-dialog-panel',
                autoFocus: false,
                restoreFocus: false,
                disableClose: true,
                data: {
                  title: 'Keine Filiale ausgewählt',
                  message:
                    'Wenn keine Filiale ausgewählt wird, erhält der Benutzer Zugriff auf alle Filialen. Möchtest du den Benutzer trotzdem anlegen?',
                  cancelText: 'Abbrechen',
                  confirmText: 'Benutzer anlegen',
                },
              });

              return confirmRef.afterClosed().pipe(
                switchMap((confirmed) => {
                  if (!confirmed) {
                    return EMPTY;
                  }

                  return this.createUserWithAssignments(result);
                })
              );
            }

            return this.createUserWithAssignments(result);
          },
        },
      });

      dialogRef.afterClosed().subscribe((saved) => {
        if (!saved) {
          return;
        }

        this.loadInitialData();

        this.snackBar.open(`Benutzer "${submittedEmail}" wurde erstellt.`, '×', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar'],
        });
      });
    });
  }

  protected editBenutzer(benutzer: Benutzer): void {
    if (this.isCopagoAdminMode()) {
      let submittedEmail = benutzer.email;

      const dialogRef = this.dialog.open(BenutzerFormDialogComponent, {
        width: '720px',
        maxWidth: 'calc(100vw - 32px)',
        panelClass: 'user-dialog-panel',
        autoFocus: false,
        restoreFocus: false,
        disableClose: true,
        data: {
          mode: 'edit',
          benutzer,
          rollen: [],
          filialen: [],
          isCopagoAdminMode: true,
          submitRequest: (result: BenutzerFormDialogResult) => {
            submittedEmail = result.email;

            return this.benutzerverwaltungService.updateSuperAdmin(
              result.tenantId ?? benutzer.id,
              {
                email: result.email,
                vorname: result.vorname,
                name: result.name,
                benutzername: result.benutzername,
              }
            );
          },
        },
      });

      dialogRef.afterClosed().subscribe((saved) => {
        if (!saved) {
          return;
        }

        this.loadInitialData();

        this.snackBar.open(`SuperAdmin "${submittedEmail}" wurde aktualisiert.`, '×', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar'],
        });
      });

      return;
    }

    this.benutzerverwaltungService
      .acquireUserLock(benutzer.id, this.getAssignedRolleIds(benutzer))
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 423) {
            return EMPTY;
          }

          this.snackBar.open(
            'Benutzer konnte nicht zum Bearbeiten gesperrt werden.',
            '×',
            {
              duration: 6000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            }
          );

          return EMPTY;
        }),
        switchMap(() =>
          forkJoin({
            rollen: this.benutzerverwaltungService.getRollen(true),
            filialen: this.benutzerverwaltungService.getFilialen(),
          })
        )
      )
      .subscribe(({ rollen, filialen }) => {
        this.rollen.set(rollen);
        this.filialen.set(filialen);

        let submittedEmail = benutzer.email;

        const dialogRef = this.dialog.open(BenutzerFormDialogComponent, {
          width: '720px',
          maxWidth: 'calc(100vw - 32px)',
          panelClass: 'user-dialog-panel',
          autoFocus: false,
          restoreFocus: false,
          disableClose: true,
          data: {
            mode: 'edit',
            benutzer,
            rollen,
            filialen,
            isCurrentUser: this.isCurrentUser(benutzer),
            submitRequest: (result: BenutzerFormDialogResult) => {
              submittedEmail = result.email;

              const updateUserRequest = this.benutzerverwaltungService.updateBenutzer(
                benutzer.id,
                {
                  email: result.email,
                  vorname: result.vorname,
                  name: result.name,
                }
              );

              if (this.isCurrentUser(benutzer)) {
                return updateUserRequest;
              }

              return updateUserRequest.pipe(
                switchMap(() =>
                  this.benutzerverwaltungService
                    .setBenutzerRollen(benutzer.id, result.rollen)
                    .pipe(
                      switchMap(() =>
                        this.benutzerverwaltungService.setBenutzerFilialen(
                          benutzer.id,
                          result.filialen
                        )
                      )
                    )
                )
              );
            },
          },
        });

        dialogRef.afterClosed().subscribe((saved) => {
          this.releaseUserEditLocks(benutzer);

          if (!saved) {
            return;
          }

          this.loadInitialData();

          this.snackBar.open(
            `Benutzer "${submittedEmail}" wurde aktualisiert.`,
            '×',
            {
              duration: 4000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['success-snackbar'],
            }
          );
        });
      });
  }

  private getAssignedRolleIds(benutzer: Benutzer): string[] {
    return benutzer.rollen.map((rolle) => rolle.id);
  }

  private releaseUserEditLocks(benutzer: Benutzer): void {
    this.benutzerverwaltungService
      .releaseUserLock(benutzer.id, this.getAssignedRolleIds(benutzer))
      .subscribe({
        error: () => {
          // Locks may already be expired or released. No UI error needed.
        },
      });
  }

  protected deleteBenutzer(benutzer: Benutzer): void {
    this.benutzerverwaltungService
      .acquireUserLock(benutzer.id)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 423) {
            const lockedBy =
              error.error?.blockedKeys?.[0]?.lockedByName ??
              'einem anderen Benutzer';

            this.snackBar.open(
              `Benutzer ist aktuell von ${lockedBy} gesperrt und kann nicht gelöscht werden.`,
              '×',
              {
                duration: 6000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['error-snackbar'],
              }
            );

            return EMPTY;
          }

          this.snackBar.open(
            'Benutzer konnte nicht zum Löschen gesperrt werden.',
            '×',
            {
              duration: 6000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            }
          );

          return EMPTY;
        })
      )
      .subscribe(() => {
        const dialogRef = this.dialog.open<
          ConfirmDialogComponent,
          ConfirmDialogData,
          boolean
        >(ConfirmDialogComponent, {
          width: '420px',
          maxWidth: 'calc(100vw - 32px)',
          panelClass: 'role-dialog-panel',
          autoFocus: false,
          restoreFocus: false,
          disableClose: true,
          data: {
            title: 'Benutzer löschen',
            message: `Benutzer "${benutzer.benutzername}" löschen?`,
            cancelText: 'Abbrechen',
            confirmText: 'Löschen',
          },
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
          if (!confirmed) {
            this.benutzerverwaltungService.releaseUserLock(benutzer.id).subscribe({
              error: () => {
                // Lock may already be expired or released. No UI error needed.
              },
            });
            return;
          }

          this.benutzerverwaltungService
            .deleteBenutzer(benutzer.id)
            .pipe(
              finalize(() => {
                this.benutzerverwaltungService.releaseUserLock(benutzer.id).subscribe({
                  error: () => {
                    // Lock may already be expired or released. No UI error needed.
                  },
                });
              })
            )
            .subscribe({
              next: () => {
                this.selectedIds.update((ids) =>
                  ids.filter((id) => id !== benutzer.id)
                );

                this.loadInitialData();

                this.snackBar.open(
                  `Benutzer "${benutzer.benutzername}" wurde gelöscht.`,
                  '×',
                  {
                    duration: 4000,
                    horizontalPosition: 'right',
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar'],
                  }
                );
              },
            });
        });
      });
  }

  protected onUserRowClick(benutzer: Benutzer): void {
    if (window.innerWidth > 720) {
      return;
    }

    this.editBenutzer(benutzer);
  }

  protected getFilialenText(benutzer: Benutzer): string {
    if (!benutzer.allowedFilialen.length) {
      return 'Alle Filialen';
    }

    return `${benutzer.allowedFilialen.length} Filiale(n)`;
  }

  protected visibleItems<T>(items: T[] | null | undefined, limit: number): T[] {
    return (items ?? []).slice(0, limit);
  }

  protected hiddenItemsCount<T>(items: T[] | null | undefined, limit: number): number {
    const hiddenCount = (items ?? []).length - limit;

    return hiddenCount > 0 ? hiddenCount : 0;
  }

  protected canSelectUser(item: Benutzer): boolean {
    return !this.isCurrentUser(item);
  }

  protected toggleSelection(item: Benutzer): void {
    if (!this.canSelectUser(item)) {
      return;
    }

    const current = this.selectedIds();

    if (current.includes(item.id)) {
      this.selectedIds.set(current.filter((id) => id !== item.id));
      return;
    }

    this.selectedIds.set([...current, item.id]);
  }

  protected isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  protected toggleSelectAll(): void {
    const selectableIds = this.filteredBenutzer()
      .filter((user) => this.canSelectUser(user))
      .map((user) => user.id);

    if (!selectableIds.length) {
      return;
    }

    const selectedIds = this.selectedIds();
    const allSelectableSelected = selectableIds.every((id) =>
      selectedIds.includes(id)
    );

    if (allSelectableSelected) {
      this.selectedIds.set(
        selectedIds.filter((id) => !selectableIds.includes(id))
      );
      return;
    }

    this.selectedIds.set([
      ...selectedIds.filter((id) => !selectableIds.includes(id)),
      ...selectableIds,
    ]);
  }

  protected isAllSelected(): boolean {
    const selectableUsers = this.filteredBenutzer().filter((user) =>
      this.canSelectUser(user)
    );

    return (
      selectableUsers.length > 0 &&
      selectableUsers.every((user) => this.selectedIds().includes(user.id))
    );
  }

  protected rollenAendern(): void {
    const selectedIds = this.selectedIds();

    if (!selectedIds.length) {
      return;
    }

    const selectedUsers = this.benutzer().filter((user) =>
      selectedIds.includes(user.id)
    );

    this.benutzerverwaltungService
      .getRollen(true)
      .pipe(
        switchMap((rollen) => {
          this.rollen.set(rollen);

          const allRolleIds = rollen.map((rolle) => rolle.id);

          return this.benutzerverwaltungService
            .acquireUserLocks(selectedIds, allRolleIds)
            .pipe(map(() => rollen));
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 423) {
            return EMPTY;
          }

          this.snackBar.open(
            'Benutzer und Rollen konnten nicht zum Bearbeiten gesperrt werden.',
            '×',
            {
              duration: 6000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            }
          );

          return EMPTY;
        })
      )
      .subscribe((rollen) => {
        const allRolleIds = rollen.map((rolle) => rolle.id);

        const dialogRef = this.dialog.open<
          RollenAendernDialogComponent,
          unknown,
          RollenAendernDialogResult
        >(RollenAendernDialogComponent, {
          width: '560px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100dvh - 32px)',
          panelClass: 'rollen-aendern-dialog-panel',
          autoFocus: false,
          restoreFocus: false,
          disableClose: true,
          data: {
            selectedUsers,
            rollen,
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (!result) {
            this.releaseBulkUserLocks(selectedIds, allRolleIds);
            return;
          }

          this.applyBulkRoleChange(result, selectedIds, allRolleIds);
        });
      });
  }

  private applyBulkRoleChange(
    result: RollenAendernDialogResult,
    lockedUserIds: string[],
    lockedRolleIds: string[]
  ): void {
    const selectedIds = this.selectedIds();

    if (!selectedIds.length) {
      this.releaseBulkUserLocks(lockedUserIds, lockedRolleIds);
      return;
    }

    const selectedRoleIds = result.selectedRoleNames
      .map((roleName) =>
        this.rollen().find((rolle) => rolle.name === roleName)?.id
      )
      .filter((roleId): roleId is string => !!roleId);

    if (!selectedRoleIds.length) {
      this.releaseBulkUserLocks(lockedUserIds, lockedRolleIds);
      return;
    }

    const action = this.mapBulkRoleAction(result.action);

    this.benutzerverwaltungService
      .bulkChangeBenutzerRollen(selectedIds, selectedRoleIds, action)
      .pipe(
        finalize(() => {
          this.releaseBulkUserLocks(lockedUserIds, lockedRolleIds);
        })
      )
      .subscribe({
        next: (response) => {
          this.selectedIds.set([]);
          this.loadInitialData();

          if (response.skipReason) {
            this.showBulkRoleWarning(response);
            return;
          }

          this.snackBar.open('Rollen wurden erfolgreich aktualisiert.', '×', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['success-snackbar'],
          });
        },
      });
  }

  private releaseBulkUserLocks(userIds: string[], rolleIds: string[] = []): void {
    this.benutzerverwaltungService.releaseUserLocks(userIds, rolleIds).subscribe({
      error: () => {
        // Locks may already be expired or released. No UI error needed.
      },
    });
  }

  private mapBulkRoleAction(action: RollenAendernDialogResult['action']): 1 | 2 | 0 {
    if (action === 'add') {
      return 1;
    }

    if (action === 'replace') {
      return 0;
    }

    return 2;
  }

  protected loadInitialData(): void {
    this.isLoading.set(true);

    this.benutzerverwaltungService.getBenutzer().subscribe({
      next: (benutzer) => {
        this.benutzer.set(benutzer);

        const uniqueRollen = this.getUniqueRollenFromUsers(benutzer);
        this.rollen.set(uniqueRollen);

        this.isLoading.set(false);
      },
      error: () => {
        this.rollen.set([]);
        this.benutzer.set([]);
        this.isLoading.set(false);
      },
    });
  }

  private getUniqueRollenFromUsers(users: Benutzer[]): Rolle[] {
    const rollenMap = new Map<string, Rolle>();

    users.forEach((user) => {
      user.rollen.forEach((rolle) => {
        if (rollenMap.has(rolle.id)) {
          return;
        }

        rollenMap.set(rolle.id, {
          id: rolle.id,
          name: rolle.name,
          istAdminRolle: rolle.istAdminRolle,
          module: rolle.module ?? [],
          benutzerAnzahl: users.filter((item) =>
            item.rollen.some((userRolle) => userRolle.id === rolle.id)
          ).length,
        });
      });
    });

    return Array.from(rollenMap.values());
  }

  private createUserWithAssignments(result: BenutzerFormDialogResult) {
    const validRolleIds = new Set(this.rollen().map((rolle) => rolle.id));
    const validFilialeIds = new Set(this.filialen().map((filiale) => filiale.id));

    const hasInvalidRolle = result.rollen.some((rolleId) => !validRolleIds.has(rolleId));
    const hasInvalidFiliale = result.filialen.some(
      (filialeId) => !validFilialeIds.has(filialeId)
    );

    if (hasInvalidRolle || hasInvalidFiliale) {
      this.snackBar.open(
        'Rollen oder Filialen sind nicht mehr gültig. Bitte Dialog schließen und erneut öffnen.',
        '×',
        {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar'],
        }
      );

      return EMPTY;
    }

    return this.benutzerverwaltungService
      .createBenutzer({
        email: result.email,
        password: result.password,
        vorname: result.vorname,
        name: result.name,
        benutzername: result.benutzername,
      })
      .pipe(
        switchMap((createdUser) =>
          this.benutzerverwaltungService
            .setBenutzerRollen(createdUser.id, result.rollen)
            .pipe(
              switchMap(() =>
                this.benutzerverwaltungService.setBenutzerFilialen(
                  createdUser.id,
                  result.filialen
                )
              ),
              catchError((error) =>
                this.benutzerverwaltungService.deleteBenutzer(createdUser.id).pipe(
                  switchMap(() => {
                    throw error;
                  }),
                  catchError(() => {
                    throw error;
                  })
                )
              )
            )
        )
      );
  }

  protected readonly hasNoRoles = computed(() => {
    if (this.authService.isCopagoAdmin()) {
      return false;
    }

    return this.authService.isSuperAdmin() && !this.hasSystemRoles();
  });

  private checkSystemRolesOnce(): void {
    if (this.hasCheckedSystemRoles) {
      return;
    }

    this.hasCheckedSystemRoles = true;
    this.checkSystemRoles(true);
  }

  protected checkSystemRoles(forceRefresh = false): void {
    if (!this.authService.isSuperAdmin()) {
      this.hasSystemRoles.set(true);
      return;
    }

    this.benutzerverwaltungService.getRollen(forceRefresh).subscribe({
      next: (rollen) => {
        const hasRoles = rollen.length > 0;

        this.hasSystemRoles.set(hasRoles);

        if (!hasRoles) {
          this.activeTab.set('rollen');
        }
      },
      error: () => {
        this.hasSystemRoles.set(true);
      },
    });
  }

  private showBulkRoleWarning(response: BulkAssignRolesResponse): void {
    const skippedUsers = [
      ...response.skippedAdmins,
      ...response.skippedLastRole,
    ];

    this.dialog.open<ErrorDialogComponent, ErrorDialogData>(ErrorDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'role-dialog-panel',
      autoFocus: false,
      restoreFocus: false,
      data: {
        title: 'Rollen konnten nicht geändert werden',
        message:
          response.skipReason || 'Die Rollenänderung wurde übersprungen.',
        chips: skippedUsers.map((user) => user.name),
      },
    });
  }

  protected readonly isCopagoAdminMode = computed(() => {
    return this.authService.isCopagoAdmin();
  });

  protected readonly currentUserId = computed(() => {
    return this.authService.getCurrentUserId();
  });

  protected isCurrentUser(item: Benutzer): boolean {
    return item.id === this.currentUserId();
  }

  protected updateTenantStatus(item: Benutzer, isActive: boolean): void {
    if (!isActive) {
      const dialogRef = this.dialog.open<
        ConfirmDialogComponent,
        ConfirmDialogData,
        boolean
      >(ConfirmDialogComponent, {
        width: '460px',
        maxWidth: 'calc(100vw - 32px)',
        panelClass: 'role-dialog-panel',
        autoFocus: false,
        restoreFocus: false,
        data: {
          title: 'Tenant deaktivieren',
          message:
            `Möchtest du den Tenant "${item.name}" wirklich deaktivieren?\n\n` +
            'Alle Benutzer dieses Tenants werden abgemeldet und können sich nicht mehr anmelden, solange der Tenant deaktiviert ist.',
          cancelText: 'Abbrechen',
          confirmText: 'Deaktivieren',
        },
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (!confirmed) {
          this.loadInitialData();
          return;
        }

        this.saveTenantStatus(item, isActive);
      });

      return;
    }

    this.saveTenantStatus(item, isActive);
  }

  private saveTenantStatus(item: Benutzer, isActive: boolean): void {
    this.benutzerverwaltungService.updateTenantStatus(item.id, isActive).subscribe({
      next: () => {
        this.benutzer.update((users) =>
          users.map((user) =>
            user.id === item.id
              ? {
                ...user,
                aktiv: isActive,
                isDeactivated: !isActive,
              }
              : user
          )
        );

        this.snackBar.open(
          `Tenant wurde ${isActive ? 'aktiviert' : 'deaktiviert'}.`,
          '×',
          {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['success-snackbar'],
          }
        );
      },
      error: () => {
        this.loadInitialData();

        this.snackBar.open('Status konnte nicht geändert werden.', '×', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar'],
        });
      },
    });
  }

  protected readonly canManageBestellungSettings = computed(() =>
    this.authService.isSuperAdmin() && !this.authService.isCopagoAdmin()
  );

  private loadBestellungSettings(): void {
    if (!this.canManageBestellungSettings()) {
      return;
    }

    this.isBestellungSettingsLoading.set(true);

    this.bestellungenService.getBestellungKonfiguration().subscribe({
      next: (config) => {
        this.orderCanBeChangedContinuously.set(
          config.orderCanBeChangedContinuously
        );
        this.isBestellungSettingsLoading.set(false);
      },
      error: () => {
        this.isBestellungSettingsLoading.set(false);

        this.snackBar.open(
          'Bestellung-Einstellungen konnten nicht geladen werden.',
          '×',
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar'],
          }
        );
      },
    });
  }

  protected updateOrderCanBeChangedContinuously(value: boolean): void {
    if (!this.canManageBestellungSettings()) {
      return;
    }

    const previousValue = this.orderCanBeChangedContinuously();

    this.orderCanBeChangedContinuously.set(value);
    this.isBestellungSettingsSaving.set(true);

    this.bestellungenService.updateBestellungKonfiguration(value).subscribe({
      next: () => {
        this.isBestellungSettingsSaving.set(false);

        this.snackBar.open(
          value
            ? 'Bestellungen können jetzt ganztägig bearbeitet werden.'
            : 'Bestellungen können jetzt nur im Zeitfenster bearbeitet werden.',
          '×',
          {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['success-snackbar'],
          }
        );
      },
      error: () => {
        this.orderCanBeChangedContinuously.set(previousValue);
        this.isBestellungSettingsSaving.set(false);

        this.snackBar.open(
          'Bestellung-Einstellung konnte nicht gespeichert werden.',
          '×',
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar'],
          }
        );
      },
    });
  }
}